import { Alert, Button, Card, EmptyState, Field, Input } from '@personachat/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins, Download, Filter } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components, paths } from '../../api/schema';
import { useSession } from '../../auth/AuthProvider';
import { DataTable, type Colonne } from '../../components/DataTable';
import { ErreurChargement, Filtre, Recherche, Section, Select } from '../../components/bits';
import { ConfirmDialog } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { enregistrer, nomDate } from '../../lib/fichiers';
import { bornesJourParis, formatDate, formatDateTime, formatDelta, formatNombre, jourParis } from '../../lib/format';
import { libelleMouvement, LIBELLE_RAISON, LIBELLE_TYPE, RAISONS, TYPES_COMPTE } from '../../lib/labels';
import { useFiltresUrl } from '../../lib/urlState';
import { PageHeader } from '../../pages/PageHeader';

import { BulkCreditForm, lireEmails } from './BulkCredit';
import { RaisonEchec } from './RaisonEchec';

type Mouvement = components['schemas']['LedgerEntry'];
type Bareme = components['schemas']['CreditSettings'];
type RequeteJournal = NonNullable<paths['/api/admin/credits/ledger']['get']['parameters']['query']>;

/** Crédits (cahier des charges §7.3) : quatre vues, une URL chacune. */
export function CreditsLayout() {
  const vues = [
    { to: '/credits', label: 'Journal', end: true },
    { to: '/credits/bilan', label: 'Bilan' },
    { to: '/credits/ajout-groupe', label: 'Ajout groupé' },
    { to: '/credits/bareme', label: 'Prix des générations' },
  ];
  return (
    <>
      <PageHeader title="Crédits" lead="Tous les mouvements de la plateforme, l’ajout groupé et le prix de chaque génération." />
      <nav className="tabs" aria-label="Vues des crédits">
        {vues.map((v) => (
          <NavLink key={v.to} to={v.to} end={v.end} className={({ isActive }) => (isActive ? 'tabs__tab tabs__tab--active' : 'tabs__tab')}>
            {v.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </>
  );
}

// ---------------------------------------------------------------------------
// Journal
// ---------------------------------------------------------------------------

const FILTRES_JOURNAL = { raison: '', sens: '', du: '', au: '', eleve: '', admin: '', page: '1' };

function requeteJournal(f: typeof FILTRES_JOURNAL): RequeteJournal {
  return {
    page: Number(f.page) || 1,
    limit: 50,
    ...(f.raison ? { reason: f.raison as RequeteJournal['reason'] } : {}),
    ...(f.sens ? { direction: f.sens as 'credit' | 'debit' } : {}),
    ...(f.du ? { from: bornesJourParis(f.du, 'debut') } : {}),
    ...(f.au ? { to: bornesJourParis(f.au, 'fin') } : {}),
    ...(f.eleve ? { q: f.eleve } : {}),
    ...(f.admin ? { adminId: f.admin } : {}),
  };
}

export function LedgerView() {
  const { valeurs: f, modifier, reinitialiser, actifs } = useFiltresUrl(FILTRES_JOURNAL);
  const toast = useToast();
  const [export_, setExport] = useState(false);

  const journal = useQuery({
    queryKey: ['credits', 'journal', f],
    queryFn: () => lire(api.GET('/api/admin/credits/ledger', { params: { query: requeteJournal(f) } })),
    placeholderData: keepPreviousData,
  });
  const admins = useQuery({ queryKey: ['admins'], queryFn: async () => (await lire(api.GET('/api/admin/admins'))).admins, staleTime: 300_000 });

  const exporter = async () => {
    setExport(true);
    try {
      const { page: _p, limit: _l, ...query } = requeteJournal(f);
      enregistrer(await lire(api.GET('/api/admin/credits/ledger/export', { params: { query }, parseAs: 'blob' })), nomDate('journal-credits'));
    } catch (err) {
      toast.erreur(messageDe(err));
    } finally {
      setExport(false);
    }
  };

  const colonnes: Colonne<Mouvement>[] = [
    { id: 'date', header: 'Date', cell: (m) => <span className="nowrap">{formatDateTime(m.createdAt)}</span> },
    {
      id: 'eleve',
      header: 'Élève',
      cell: (m) =>
        m.user ? (
          <Link className="link" to={`/eleves/${m.user.id}?onglet=credits`}>
            {m.user.email}
          </Link>
        ) : (
          '—'
        ),
    },
    { id: 'mouvement', header: 'Mouvement', align: 'right', cell: (m) => <span className={`num ${m.delta > 0 ? 'pos' : 'neg'}`}>{formatDelta(m.delta)}</span> },
    {
      id: 'raison',
      header: 'Raison',
      cell: (m) => (
        <div>
          <div>{libelleMouvement(m)}</div>
          <RaisonEchec m={m} />
          {m.motif && <div className="cell-sub">« {m.motif} »</div>}
        </div>
      ),
    },
    { id: 'auteur', header: 'Auteur', secondaire: true, cell: (m) => m.admin?.email ?? <span className="muted">Automatique</span> },
    { id: 'solde', header: 'Solde après', align: 'right', secondaire: true, cell: (m) => <span className="num">{formatNombre(m.balanceAfter)}</span> },
  ];

  return (
    <>
      <div className="toolbar">
        <Recherche id="journal-eleve" label="E-mail de l’élève" valeur={f.eleve} onChange={(eleve) => modifier({ eleve })} placeholder="E-mail de l’élève" />
        <Filtre id="j-raison" label="Raison">
          <Select id="j-raison" value={f.raison} onChange={(e) => modifier({ raison: e.target.value })}>
            <option value="">Toutes</option>
            {RAISONS.map((r) => (
              <option key={r} value={r}>
                {LIBELLE_RAISON[r]}
              </option>
            ))}
          </Select>
        </Filtre>
        <Filtre id="j-sens" label="Sens">
          <Select id="j-sens" value={f.sens} onChange={(e) => modifier({ sens: e.target.value })}>
            <option value="">Tous</option>
            <option value="credit">Entrées</option>
            <option value="debit">Sorties</option>
          </Select>
        </Filtre>
        <Filtre id="j-du" label="Du">
          <Input id="j-du" type="date" value={f.du} max={f.au || jourParis()} onChange={(e) => modifier({ du: e.target.value })} />
        </Filtre>
        <Filtre id="j-au" label="Au">
          <Input id="j-au" type="date" value={f.au} min={f.du || undefined} max={jourParis()} onChange={(e) => modifier({ au: e.target.value })} />
        </Filtre>
        <Filtre id="j-admin" label="Auteur">
          <Select id="j-admin" value={f.admin} onChange={(e) => modifier({ admin: e.target.value })}>
            <option value="">Tous</option>
            {(admins.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.email}
              </option>
            ))}
          </Select>
        </Filtre>
        {actifs > 0 && (
          <Button variant="ghost" size="sm" icon={<Filter size={14} aria-hidden="true" />} onClick={reinitialiser}>
            Effacer ({actifs})
          </Button>
        )}
        <span className="toolbar__spacer" />
        <Button icon={<Download size={15} aria-hidden="true" />} onClick={() => void exporter()} loading={export_}>
          Exporter
        </Button>
      </div>

      {journal.isError ? (
        <ErreurChargement erreur={journal.error} onReessayer={() => void journal.refetch()} />
      ) : (
        <DataTable
          libelle="Journal des crédits"
          colonnes={colonnes}
          lignes={journal.data?.entries}
          cleLigne={(m) => m.id}
          chargement={journal.isFetching && !journal.data}
          pagination={journal.data ? { page: journal.data.pagination.page, pages: journal.data.pagination.pages, total: journal.data.pagination.total } : undefined}
          onPage={(page) => modifier({ page: String(page) })}
          vide={<EmptyState icon={<Coins size={26} aria-hidden="true" />} title="Aucun mouvement" text={actifs ? 'Aucun mouvement ne correspond aux filtres.' : undefined} />}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Bilan
// ---------------------------------------------------------------------------

export function SummaryView() {
  const { valeurs: f, modifier } = useFiltresUrl({ periode: '30' });
  const bilan = useQuery({
    queryKey: ['credits', 'bilan', f.periode],
    queryFn: () =>
      lire(
        api.GET('/api/admin/credits/summary', {
          params: { query: { from: new Date(Date.now() - Number(f.periode) * 24 * 3600 * 1000).toISOString() } },
        })
      ),
    placeholderData: keepPreviousData,
  });

  const s = bilan.data?.summary;
  const tuiles = s
    ? [
        { label: 'Émis', valeur: s.issued, aide: 'Inscription, achats, abonnements, ajouts admin' },
        { label: 'Consommés', valeur: s.consumed, aide: 'Images et vidéos générées' },
        { label: 'Remboursés', valeur: s.refunded, aide: 'Générations échouées rendues' },
        { label: 'Retirés', valeur: s.removed, aide: 'Retraits faits par un admin' },
        { label: 'En circulation', valeur: s.inCirculation, aide: `Aujourd’hui, sur ${formatNombre(s.wallets)} porte-monnaie` },
      ]
    : null;

  return (
    <>
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Période">
          {['7', '30', '90', '365'].map((p) => (
            <button key={p} type="button" aria-pressed={f.periode === p} onClick={() => modifier({ periode: p })}>
              {p === '365' ? '12 mois' : `${p} jours`}
            </button>
          ))}
        </div>
        {bilan.data && (
          <span className="muted small">
            Du {formatDate(bilan.data.period.from)} au {formatDate(bilan.data.period.to)}
          </span>
        )}
      </div>
      {bilan.isError ? (
        <ErreurChargement erreur={bilan.error} onReessayer={() => void bilan.refetch()} />
      ) : (
        <>
          <div className="kpis">
            {(tuiles ?? Array.from({ length: 5 }, () => null)).map((t, i) => (
              <Card key={t?.label ?? i} className="kpi">
                <div className="kpi__label">{t?.label ?? '…'}</div>
                <div className="kpi__value">{t ? formatNombre(t.valeur) : '—'}</div>
                {t && <div className="kpi__hint">{t.aide}</div>}
              </Card>
            ))}
          </div>
          <Section title="Par raison">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Raison</th>
                    <th style={{ textAlign: 'right' }}>Entrées</th>
                    <th style={{ textAlign: 'right' }}>Sorties</th>
                    <th style={{ textAlign: 'right' }}>Mouvements</th>
                  </tr>
                </thead>
                <tbody>
                  {(bilan.data?.byReason ?? []).map((r) => (
                    <tr key={r.reason}>
                      <td>{LIBELLE_RAISON[r.reason]}</td>
                      <td className="num pos" style={{ textAlign: 'right' }}>
                        {r.credited ? formatDelta(r.credited) : '—'}
                      </td>
                      <td className="num neg" style={{ textAlign: 'right' }}>
                        {r.debited ? formatDelta(-r.debited) : '—'}
                      </td>
                      <td className="num" style={{ textAlign: 'right' }}>
                        {formatNombre(r.count)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bilan.data && bilan.data.byReason.length === 0 && <EmptyState title="Aucun mouvement sur la période" />}
            </div>
          </Section>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Ajout groupé
// ---------------------------------------------------------------------------

export function BulkView() {
  const id = useId();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [texte, setTexte] = useState('');
  const emails = lireEmails(texte);

  return (
    <Card>
      <div className="stack">
        <p className="muted" style={{ margin: 0 }}>
          Collez les e-mails des élèves (un par ligne, ou séparés par des virgules). Pour partir d’une liste filtrée, sélectionnez les élèves dans
          l’écran <Link className="link" to="/eleves">Élèves</Link> puis « Ajouter des crédits ».
        </p>
        <Field id={`${id}-emails`} label="E-mails" hint={emails.length ? `${formatNombre(emails.length)} adresse(s) saisie(s).` : '5 000 au plus par envoi.'}>
          <textarea
            id={`${id}-emails`}
            className="ui-input textarea"
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            placeholder={'awa.diallo@exemple.fr\nfatou.traore@exemple.fr'}
            rows={8}
          />
        </Field>
        <BulkCreditForm
          destinataires={{ emails }}
          onTermine={(n) => {
            toast.succes(`${formatNombre(n)} élève${n > 1 ? 's' : ''} créditée${n > 1 ? 's' : ''}.`);
            void queryClient.invalidateQueries({ queryKey: ['credits'] });
            void queryClient.invalidateQueries({ queryKey: ['eleves'] });
            void queryClient.invalidateQueries({ queryKey: ['eleve'] });
          }}
        />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Barème
// ---------------------------------------------------------------------------

const COUTS: Array<{ cle: keyof Bareme['costs']; libelle: string; aide: string }> = [
  { cle: 'image', libelle: 'Image', aide: 'Une image générée en qualité standard' },
  { cle: 'imageHd', libelle: 'Image HD', aide: 'Une image en qualité HD, environ 4 fois plus chère à produire' },
  { cle: 'moodboard', libelle: 'Moodboard', aide: 'Un moodboard' },
  { cle: 'logo', libelle: 'Logo', aide: 'Une proposition de logo' },
  { cle: 'palette', libelle: 'Palette', aide: 'Une palette de couleurs' },
  { cle: 'videoPerSecond', libelle: 'Seconde de vidéo', aide: 'Une vidéo de 5 s coûte 5 fois cette valeur' },
];

export function RatesView() {
  const { user } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const lectureSeule = !user?.isSuperAdmin;
  const [valeurs, setValeurs] = useState<Bareme | null>(null);
  const [confirmer, setConfirmer] = useState(false);

  const bareme = useQuery({ queryKey: ['credits', 'bareme'], queryFn: () => lire(api.GET('/api/admin/settings/credits')) });
  useEffect(() => {
    if (bareme.data) setValeurs(bareme.data.settings);
  }, [bareme.data]);

  if (bareme.isError) return <ErreurChargement erreur={bareme.error} onReessayer={() => void bareme.refetch()} />;
  if (!bareme.data || !valeurs) return <Card>Chargement…</Card>;

  const actuel = bareme.data.settings;
  const changements = [
    ...COUTS.filter((c) => valeurs.costs[c.cle] !== actuel.costs[c.cle]).map((c) => `${c.libelle} : ${actuel.costs[c.cle]} → ${valeurs.costs[c.cle]}`),
    ...TYPES_COMPTE.filter((t) => valeurs.signupGrants[t] !== actuel.signupGrants[t]).map(
      (t) => `Inscription ${LIBELLE_TYPE[t]} : ${actuel.signupGrants[t]} → ${valeurs.signupGrants[t]}`
    ),
  ];
  const nombre = (v: string) => (v === '' ? 0 : Math.min(1000, Number(v.replace(/[^\d]/g, '')) || 0));

  const soumettre = (e: FormEvent) => {
    e.preventDefault();
    if (changements.length) setConfirmer(true);
  };

  return (
    <form onSubmit={soumettre} className="stack">
      {lectureSeule && <Alert tone="info">Lecture seule : seul un super admin peut modifier le barème.</Alert>}
      <p className="note" style={{ margin: 0 }}>
        {bareme.data.source === 'base'
          ? `Barème enregistré depuis le back-office${bareme.data.updatedAt ? ` le ${formatDateTime(bareme.data.updatedAt)}` : ''}.`
          : 'Barème par défaut du serveur : aucune modification enregistrée depuis le back-office.'}{' '}
        Une modification s’applique aux générations suivantes, sans redéploiement.
      </p>
      <div className="grid-2 bareme">
        <Card>
          <h3 className="section__title" style={{ marginBottom: 12 }}>
            Coût d’une génération (crédits)
          </h3>
          <div className="stack">
            {COUTS.map((c) => (
              <Field key={c.cle} id={`bareme-${c.cle}`} label={c.libelle} hint={c.aide}>
                <Input
                  id={`bareme-${c.cle}`}
                  inputMode="numeric"
                  value={String(valeurs.costs[c.cle])}
                  disabled={lectureSeule}
                  onChange={(e) => setValeurs({ ...valeurs, costs: { ...valeurs.costs, [c.cle]: nombre(e.target.value) } })}
                />
              </Field>
            ))}
          </div>
        </Card>
        <Card>
          <h3 className="section__title" style={{ marginBottom: 12 }}>
            Crédits offerts à l’inscription
          </h3>
          <div className="stack">
            {TYPES_COMPTE.map((t) => (
              <Field key={t} id={`bareme-${t}`} label={`Compte ${LIBELLE_TYPE[t]}`}>
                <Input
                  id={`bareme-${t}`}
                  inputMode="numeric"
                  value={String(valeurs.signupGrants[t])}
                  disabled={lectureSeule}
                  onChange={(e) => setValeurs({ ...valeurs, signupGrants: { ...valeurs.signupGrants, [t]: nombre(e.target.value) } })}
                />
              </Field>
            ))}
            <p className="note">Appliqué aux inscriptions suivantes ; les comptes existants ne changent pas.</p>
          </div>
        </Card>
      </div>
      {!lectureSeule && (
        <div className="row">
          <Button type="submit" variant="primary" disabled={changements.length === 0}>
            Enregistrer le barème
          </Button>
          {changements.length > 0 && (
            <Button onClick={() => setValeurs(actuel)} variant="ghost">
              Annuler les modifications
            </Button>
          )}
        </div>
      )}
      <ConfirmDialog
        open={confirmer}
        onOpenChange={setConfirmer}
        title="Modifier le barème"
        confirmLabel="Enregistrer"
        onConfirm={async () => {
          await lire(api.PUT('/api/admin/settings/credits', { body: valeurs }));
          await queryClient.invalidateQueries({ queryKey: ['credits', 'bareme'] });
          toast.succes('Barème enregistré, appliqué immédiatement.');
          navigate('/credits/bareme', { replace: true });
        }}
      >
        <ul className="plain-list">
          {changements.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <p className="note">Les générations et inscriptions suivantes utilisent ces valeurs, sur l’application élève comme dans le chat.</p>
      </ConfirmDialog>
    </form>
  );
}
